import React, { useContext, useEffect, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { Alert, Box, Button, ButtonBase, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { LensMark } from '~/components/brand';

import { legacyLensUrl } from '~/pages/AskRichardUnified/legacyLensUrl';
import { useDebugDetailsFlag } from '~/pages/AskRichardUnified/uiStream/flags';
import { paneTokens } from '~/pages/AskRichardUnified/uiStream/paneTokens';

import { encodedJsonBody } from '~/hooks/apis/reporting/encodedJsonBody';
import {
  reportingLeLensQueryKeys,
  useGetReportingLeLens,
  useUpdateReportingLeLens,
} from '~/hooks/apis/reporting/useGetReportingLeLens';
import { useLensCollaborationMutation } from '~/hooks/apis/reporting/useLensCollaboration';

import type ApiError from '~/types/ApiError';
import { type Lens, type LensCard, type LensCardData, type LensGovernedMeasureMetadata } from '~/types/leLens';

import { expandLensCardResponse } from '~/utils/leLensTransport';
import { requestWithTimeout } from '~/utils/requestWithTimeout';
import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import LensNativeDraftStructurePreview from './LensNativeDraftStructurePreview';
import LensReviewProposalPreview from './LensReviewProposalPreview';
import LensStructuralProposalPreview from './LensStructuralProposalPreview';
import { cardComparison } from './cardComparison';
import LensCardView from './cards/LensCardView';
import CustomViewCodeDiff from './customView/CustomViewCodeDiff';
import { prepareFields } from './datasetFields';
import { discardLensDraft } from './discardLensDraft';
import { GovernedMeasureMetadataProvider } from './governedMeasureMetadata';
import { showCardOnBoard } from './lensCardDom';
import { editableLensDefinition, lensChangeSummary, lensDefinitionChanges } from './lensDefinitionChanges';
import { useLensEditGate } from './lensEditGate';
import { type LensProposal, globalLensProposalKey, lensProposalKey } from './lensProposal';
import { ProposalCollapse } from './proposalCollapse';
import { customViewPresentationChanged } from './reviewLensCandidate';

function overriddenLabel(path: string, lens: Lens, draft?: LensProposal['definition']) {
  const [scope, id, key] = path.split('.');
  if (scope === 'cards' || scope === 'datasets') {
    const kept = (lens.spec[scope] ?? []).find((entry) => entry.id === id);
    const drafted = (draft?.spec[scope] ?? []).find((entry) => entry.id === id);
    const known = kept ?? drafted;
    const name = `“${(known && ('title' in known ? known.title : known.name)) || id}”`;
    if (key) {
      return `${name} ${key}`;
    }
    return kept ? `${name} ${scope === 'cards' ? 'card' : 'dataset'}` : `removal of ${name}`;
  }
  return scope === 'spec' ? id : scope;
}

/** Custom view code is shown as a line diff (CustomViewCodeDiff), not as two full copies of the code. */
const CUSTOM_VIEW_CODE_FIELD = /^spec\.customView(\.(html|css|javascript))?$/;
const CUSTOM_VIEW_PARTS = ['html', 'css', 'javascript'] as const;

/** What changed on one card, as short labels, read from a diff of that card alone. */
function cardChangeLines(lens: Lens, draft: LensProposal['definition'] | undefined, card: LensCard): Array<string> {
  if (!draft) {
    return [];
  }
  const before = lens.spec.cards.find((existing) => existing.id === card.id);
  const after = draft.spec.cards.find((existing) => existing.id === card.id) ?? card;
  if (!before) {
    return [];
  }
  return lensDefinitionChanges(before, after)
    .map((change) => {
      const label = change.field
        .replace(/^config\./, '')
        .replace(/\./g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .toLowerCase();
      return change.before && change.before !== '—'
        ? `${label} ${change.before} → ${change.after}`
        : `${label} ${change.after}`;
    })
    .slice(0, 3);
}

function SavedLine({ label }: { label: string }) {
  const theme = useTheme();
  const pane = paneTokens(theme);
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      data-testid="lens-proposal-saved"
      sx={{ px: 1.5, py: 1, border: `1px solid ${pane.hairline}`, borderRadius: 0.75 }}
    >
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          bgcolor: pane.accent,
          color: pane.accentContrast,
          display: 'grid',
          placeItems: 'center',
          fontSize: 10,
          fontWeight: 700,
          flex: 'none',
        }}
      >
        ✓
      </Box>
      <Typography sx={{ fontSize: 13 }}>{label}</Typography>
    </Stack>
  );
}

export default function LensProposalPreview(props: React.ComponentProps<typeof LensEditProposalPreview>) {
  return props.proposal.purpose === 'review_proposal' ? (
    <LensReviewProposalPreview {...props} />
  ) : (
    <LensEditProposalPreview {...props} />
  );
}

function LensEditProposalPreview({
  proposal,
  onSavedLens,
  onDiscard,
  hasUnsavedChanges = false,
  globalDraft = false,
  globalSuperseded = false,
}: {
  proposal: LensProposal;
  onSavedLens?: (lens: Lens) => void;
  onDiscard?: () => void;
  hasUnsavedChanges?: boolean;
  /** An unbound conversation names its verified target and requires edit access for candidates. */
  globalDraft?: boolean;
  globalSuperseded?: boolean;
}) {
  const client = useQueryClient();
  const debugDetails = useDebugDetailsFlag();
  const theme = useTheme();
  const pane = paneTokens(theme);
  const editGate = useLensEditGate();
  const { data, isPending } = useGetReportingLeLens(proposal.lensId);
  const update = useUpdateReportingLeLens();
  const submit = useLensCollaborationMutation(proposal.lensId);
  const [submitted, setSubmitted] = useState(false);
  const [initial] = useState(() => {
    const own = globalDraft ? client.getQueryData<LensProposal>(globalLensProposalKey(proposal)) : undefined;
    const cached = client.getQueryData<LensProposal>(lensProposalKey(proposal.lensId));
    return own ?? (cached?.proposalId === proposal.proposalId ? cached : proposal);
  });
  const cards = initial.cards;
  const [status, setStatus] = useState(initial.status);
  const [error, setError] = useState('');
  const [discarding, setDiscarding] = useState(false);
  const [conflicted, setConflicted] = useState(false);
  const lens = data?.lens;
  const canEdit = data?.access.canEdit ?? false;
  const canPropose = data?.access.canPropose ?? canEdit;
  const canReviewDraft = globalDraft ? canEdit : canPropose;
  const latest = useQuery<LensProposal | null>({
    queryKey: globalDraft ? globalLensProposalKey(proposal) : lensProposalKey(proposal.lensId),
    queryFn: () => null,
    enabled: false,
    ...(globalDraft ? { gcTime: Infinity } : {}),
  });
  const superseded = globalDraft
    ? globalSuperseded
    : Boolean(latest.data && latest.data.createdAt > proposal.createdAt);
  const candidate = useQuery({
    queryKey: ['lens-proposal-candidate', proposal.candidateId, lens?.version],
    enabled: Boolean(proposal.candidateId && lens) && canReviewDraft && !status && !superseded,
    retry: false,
    queryFn: () =>
      requestWithTimeout<{
        status: 'current' | 'conflict' | 'expired' | 'discarded';
        definition?: LensProposal['definition'];
        governedMeasureMetadata?: LensGovernedMeasureMetadata;
        overridden?: Array<string>;
        appliedVersion?: number;
      }>(
        resolveReportingLeLensUrl(`/api/reporting/le-lenses/${proposal.lensId}/changes`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: encodedJsonBody({ candidateId: proposal.candidateId, action: 'inspect' }),
        },
        'The draft did not load. Try again.',
      ),
  });
  const matchesLens = (definition?: LensProposal['definition']) =>
    Boolean(
      lens &&
      definition &&
      !lensDefinitionChanges(editableLensDefinition(lens), editableLensDefinition(definition)).length,
    );
  const candidateCurrent = candidate.data?.status === 'current';
  const candidateDiscarded = candidate.data?.status === 'discarded';
  const overridden = (candidateCurrent && candidate.data?.overridden) || [];
  const candidateApplied = candidateCurrent && Boolean(candidate.data?.appliedVersion);
  const noChanges =
    candidateCurrent && !candidateApplied && !overridden.length && matchesLens(candidate.data?.definition);
  const nothingLeft =
    candidateCurrent && !candidateApplied && overridden.length > 0 && matchesLens(candidate.data?.definition);
  const rebasedDefinition = candidateCurrent ? candidate.data?.definition : undefined;
  const lensInputs = (definition: NonNullable<LensProposal['definition']>) => ({
    filters: definition.spec.filters,
    period: definition.spec.period,
    periodDefault: definition.spec.periodDefault,
    calendar: definition.spec.calendar,
  });
  const cardInputs = (definition: NonNullable<LensProposal['definition']>, card: LensCard) => {
    const { spec } = editableLensDefinition(definition);
    return {
      card: spec.cards.find((entry) => entry.id === card.id),
      dataset: spec.datasets?.find((entry) => entry.id === card.datasetId),
      ...lensInputs(definition),
    };
  };
  const shownCards = rebasedDefinition
    ? rebasedDefinition.spec.cards.filter(
        (card) =>
          !lens || lensDefinitionChanges(cardInputs(lens, card), cardInputs(rebasedDefinition, card)).length > 0,
      )
    : cards;
  const previews = useQuery({
    queryKey: [
      'lens-proposal-preview',
      proposal.proposalId,
      shownCards,
      proposal.candidateId ? lens?.version : undefined,
    ],
    enabled:
      canReviewDraft &&
      !conflicted &&
      !status &&
      !superseded &&
      !hasUnsavedChanges &&
      (!proposal.candidateId || (candidateCurrent && !candidateApplied && !noChanges && !nothingLeft)),
    retry: false,
    queryFn: async () => {
      const results: Array<LensCardData> = [];
      for (const card of shownCards) {
        const result = await requestWithTimeout<Parameters<typeof expandLensCardResponse>[0]>(
          resolveReportingLeLensUrl(
            `/api/reporting/le-lenses/${proposal.lensId}/${proposal.candidateId ? 'changes' : 'preview'}`,
          ),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: encodedJsonBody(
              proposal.candidateId
                ? { candidateId: proposal.candidateId, cardId: card.id, action: 'preview' }
                : { version: proposal.baseVersion, card, filters: {} },
            ),
          },
          'The preview did not finish. Try again.',
          90_000,
        );
        results.push(expandLensCardResponse(result).card);
      }
      return results;
    },
  });
  const refusedWith = (code: number) => (previews.error as ApiError | null)?.status === code;
  const outOfDate = conflicted || nothingLeft || candidate.data?.status === 'conflict' || refusedWith(409);
  const expired = candidate.data?.status === 'expired' || refusedWith(404);
  const applied = candidateApplied;
  const draft = candidate.data?.definition ?? proposal.definition;
  const previewDatasets = draft?.spec.datasets ?? lens?.spec.datasets ?? [];
  const previewMeasureMetadata = candidate.data?.definition
    ? candidate.data.governedMeasureMetadata
    : data?.governedMeasureMetadata;
  // Persisted History receipts can omit the full draft; only inspection may restore it.
  const inspectedDefinition =
    canReviewDraft && candidateCurrent && !candidate.isFetching ? candidate.data?.definition : undefined;
  const draftCardCount = proposal.candidateId
    ? inspectedDefinition?.spec.cards.length
    : (proposal.definition?.spec.cards.length ?? shownCards.length);
  const activeDefinition = proposal.candidateId
    ? !candidateApplied && !outOfDate
      ? inspectedDefinition
      : undefined
    : proposal.definition;
  useEffect(() => {
    if (!proposal.proposalId || (globalDraft && outOfDate)) {
      return;
    }
    const key = globalDraft ? globalLensProposalKey(proposal) : lensProposalKey(proposal.lensId);
    const current = client.getQueryData<LensProposal>(key);
    if (!current || current.createdAt <= proposal.createdAt) {
      client.setQueryData(key, {
        ...proposal,
        definition: globalDraft ? (inspectedDefinition ?? proposal.definition) : activeDefinition,
        cards,
        status,
      });
    }
  }, [client, proposal, inspectedDefinition, activeDefinition, cards, status, outOfDate, globalDraft]);
  useEffect(() => {
    if (!candidateDiscarded) return;
    setStatus('discarded');
    client.setQueryData(globalDraft ? globalLensProposalKey(proposal) : lensProposalKey(proposal.lensId), {
      ...proposal,
      cards,
      status: 'discarded',
    });
  }, [candidateDiscarded, client, globalDraft, proposal, cards]);
  const fieldFaults = (proposal.definition?.spec.datasets ?? []).flatMap(
    (dataset) =>
      prepareFields(
        dataset.fields,
        dataset.columns.filter((column) => column.kind !== 'calculated').map((column) => column.name),
      ).faults,
  );
  const rechecking = Boolean(proposal.candidateId) && (!candidateCurrent || candidate.isFetching);
  const previewReady =
    !rechecking &&
    !fieldFaults.length &&
    previews.data?.length === shownCards.length &&
    previews.data?.every((card) => ['ready', 'empty'].includes(card.status) && !card.truncated);
  const busy = discarding || submit.isPending || update.isPending || previews.isFetching;
  const finish = (next: LensProposal['status']) => {
    setStatus(next);
    if (next === 'discarded') {
      onDiscard?.();
    }
    client.setQueryData(globalDraft ? globalLensProposalKey(proposal) : lensProposalKey(proposal.lensId), {
      ...proposal,
      cards,
      status: next,
    });
  };
  const save = async () => {
    if (!lens || !canReviewDraft || outOfDate || noChanges || !previewReady || hasUnsavedChanges) {
      return;
    }
    setError('');
    try {
      if (proposal.candidateId) {
        const response = await submit.mutateAsync({
          suffix: 'changes',
          body: { candidateId: proposal.candidateId, action: 'apply' },
        });
        if (!response.lens) {
          throw new Error('The candidate was not saved');
        }
        finish('saved');
        await client.invalidateQueries({ queryKey: reportingLeLensQueryKeys.detail(proposal.lensId) });
        onSavedLens?.(response.lens);
        return;
      }
      if (!canEdit) {
        await submit.mutateAsync({
          suffix: 'proposals',
          body: {
            baseVersion: proposal.baseVersion,
            summary: `Update ${cards.map((card) => card.title).join(', ')}`.slice(0, 2000),
            definition: {
              title: lens.title,
              description: lens.description,
              spec: {
                ...lens.spec,
                cards: lens.spec.cards.map((card) => cards.find((edit) => edit.id === card.id) ?? card),
              },
            },
          },
        });
        setSubmitted(true);
        finish('discarded');
        return;
      }
      const response = await update.mutateAsync({
        id: lens.id,
        title: lens.title,
        description: lens.description,
        version: proposal.baseVersion,
        spec: { ...lens.spec, cards: lens.spec.cards.map((card) => cards.find((edit) => edit.id === card.id) ?? card) },
        quietSuccess: true,
        quietFailure: true,
        refetchData: true,
      });
      finish('saved');
      onSavedLens?.(response.lens);
    } catch (failure) {
      if ((failure as ApiError).status === 409) {
        setConflicted(true);
        return;
      }
      setError(failure instanceof Error ? failure.message : 'Could not save the change');
    }
  };
  const discard = async () => {
    setError('');
    setDiscarding(true);
    try {
      await discardLensDraft(
        proposal,
        (body) =>
          requestWithTimeout<{ status: string }>(
            resolveReportingLeLensUrl(`/api/reporting/le-lenses/${proposal.lensId}/changes`),
            { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: encodedJsonBody(body) },
            'Could not discard the draft. Try again.',
          ),
        () => finish('discarded'),
      );
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Could not discard the draft');
    } finally {
      setDiscarding(false);
    }
  };
  const reportCollapse = useContext(ProposalCollapse);
  const collapsed = !proposal.proposalId || submitted || Boolean(status) || superseded;
  useEffect(() => reportCollapse?.(collapsed), [collapsed, reportCollapse]);
  if (!proposal.proposalId) {
    return <Typography>This older preview is unavailable. Ask for the change again.</Typography>;
  }
  if (submitted) {
    return <Typography>Proposal submitted for the owner to review.</Typography>;
  }
  if (status) {
    return (
      <SavedLine
        label={
          status === 'saved'
            ? `Saved${globalDraft && lens ? ` to “${lens.title}”` : ''} · ${cards.length} card${cards.length === 1 ? '' : 's'} updated`
            : `Discarded${globalDraft && lens ? ` changes to “${lens.title}”` : ''}`
        }
      />
    );
  }
  if (superseded) {
    return <Typography variant="body2">A newer proposal is available below.</Typography>;
  }
  if (isPending) {
    return <Typography>{globalDraft ? 'Checking the target Lens and your access…' : 'Loading Lens…'}</Typography>;
  }
  if (!lens) {
    return <Alert severity="error">Could not load this Lens.</Alert>;
  }
  if (!canReviewDraft) {
    return (
      <Alert severity="info">
        {globalDraft
          ? `You do not have permission to save changes to “${lens.title}”.`
          : 'You do not have access to propose changes to this Lens.'}
      </Alert>
    );
  }
  if (hasUnsavedChanges) {
    return <Alert severity="info">Save or discard your current card edits before reviewing this change.</Alert>;
  }
  if (applied) {
    return <SavedLine label={globalDraft ? `Saved to “${lens.title}”` : 'Saved'} />;
  }
  if (noChanges) {
    return <Typography variant="body2">No changes to save.</Typography>;
  }
  if (outOfDate || expired) {
    return (
      <Typography variant="body2">
        {nothingLeft
          ? 'The Lens changed after this draft. Nothing in it is left to save.'
          : 'This draft no longer applies to the Lens. Ask again.'}
      </Typography>
    );
  }
  return (
    <Stack spacing={1}>
      {globalDraft && (
        <Stack spacing={0.25} data-testid="lens-proposal-target">
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Typography variant="subtitle2">Changes to “{lens.title}”</Typography>
            <Button
              size="small"
              href={legacyLensUrl(lens.id, '')}
              target="_blank"
              rel="noreferrer"
              sx={{ flex: 'none' }}
            >
              Open Lens
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            You can edit this Lens. Review the changes before saving.
          </Typography>
        </Stack>
      )}
      {overridden.length > 0 && (
        <Alert severity="info">
          The Lens changed after this draft. It keeps its own{' '}
          {overridden.map((path) => overriddenLabel(path, lens, proposal.definition)).join(', ')}; the rest of the draft
          is below.
        </Alert>
      )}
      {fieldFaults.map((fault) => (
        <Alert key={fault.name} severity="error">
          {fault.name}: {fault.reason}
        </Alert>
      ))}

      {!globalDraft && canEdit && candidateCurrent && candidate.data?.definition && (
        <LensNativeDraftStructurePreview lens={lens} definition={candidate.data.definition} />
      )}
      {globalDraft && draft && customViewPresentationChanged(lens.spec.customView, draft.spec.customView) && (
        <LensStructuralProposalPreview lens={lens} definition={draft} reviewKind="draft" />
      )}

      {debugDetails && draft && (
        <details>
          <summary>Definition changes</summary>
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {lensDefinitionChanges(editableLensDefinition(lens), editableLensDefinition(draft))
              .filter((change) => !CUSTOM_VIEW_CODE_FIELD.test(change.field))
              .map((change) => (
                <Box key={change.field} sx={{ mb: 1, overflowWrap: 'anywhere' }}>
                  <Typography variant="subtitle2">{change.field}</Typography>
                  <Typography variant="body2">Before: {change.before}</Typography>
                  <Typography variant="body2">After: {change.after}</Typography>
                </Box>
              ))}
            {CUSTOM_VIEW_PARTS.map((part) => {
              const before = lens.spec.customView?.[part] ?? '';
              const after = draft?.spec.customView?.[part] ?? '';
              return before === after ? null : (
                <CustomViewCodeDiff key={part} part={part} before={before} after={after} />
              );
            })}
          </Box>
        </details>
      )}
      <Stack
        data-testid="lens-proposal-changes"
        sx={{ border: `1px solid ${pane.hairline}`, borderRadius: 0.75, overflow: 'hidden' }}
      >
        {shownCards.map((card, index) => {
          const preview = previews.data?.find((item) => item.cardId === card.id);
          const isNew = !lens.spec.cards.some((existing) => existing.id === card.id);
          const previewFailed =
            Boolean(previews.error) || (preview ? !['ready', 'empty'].includes(preview.status) : false);
          const previewPending = !previews.error && !preview && (previews.isFetching || previews.isPending);
          const changeLines = cardChangeLines(lens, draft, card);
          return (
            <Box key={card.id} sx={{ borderTop: index === 0 ? 0 : `1px solid ${pane.hairline}` }}>
              {isNew ? (
                // A new card is the one thing that gets a preview: it is not on the board yet.
                <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5 }}>
                  <Typography
                    sx={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.05em', color: pane.accent, mb: 0.5 }}
                  >
                    NEW
                  </Typography>
                  <Box sx={{ overflow: 'auto' }}>
                    <GovernedMeasureMetadataProvider savedDatasets={previewDatasets} metadata={previewMeasureMetadata}>
                      <LensCardView
                        card={card}
                        data={preview}
                        comparison={cardComparison(preview)}
                        datasets={previewDatasets}
                        canEdit={false}
                        showActions={false}
                        isFetchingData={previews.isFetching}
                        dateRange={preview?.period?.current ?? {}}
                        hasActiveFilters={false}
                        onClearFilters={() => {}}
                        onDelete={async () => {}}
                        onEdit={() => {}}
                        onRefresh={() => {}}
                        onSelect={() => {}}
                        isRefreshing={false}
                        insightRefresh={0}
                        span={8}
                      />
                    </GovernedMeasureMetadataProvider>
                  </Box>
                </Box>
              ) : null}
              <Stack
                alignItems="center"
                direction="row"
                spacing={1.25}
                sx={{ px: 1.5, py: 1.125, minWidth: 0 }}
                data-testid="lens-proposal-change-row"
              >
                <LensMark size={14} inverted={theme.palette.mode === 'dark'} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600 }} title={card.title}>
                    {card.title}
                    {isNew ? (
                      <Typography
                        component="span"
                        sx={{
                          ml: 0.75,
                          fontSize: 10.5,
                          fontWeight: 600,
                          letterSpacing: '0.04em',
                          color: pane.accent,
                          border: `1px solid ${pane.accent}`,
                          borderRadius: 0.5,
                          px: 0.5,
                          verticalAlign: '1px',
                        }}
                      >
                        NEW CARD
                      </Typography>
                    ) : null}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: 12.5, color: 'text.secondary' }} title={changeLines.join(' · ')}>
                    {changeLines.join(' · ') || (isNew ? `${card.type} card` : 'Changed')}
                  </Typography>
                </Box>
                {previewFailed ? (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 'none' }}>
                    <Typography sx={{ fontSize: 12.5, color: 'error.main' }}>Preview failed</Typography>
                    <ButtonBase
                      onClick={() => void previews.refetch()}
                      sx={{ fontSize: 12.5, fontWeight: 500, color: pane.accent, borderRadius: 0.5 }}
                    >
                      Retry
                    </ButtonBase>
                  </Stack>
                ) : previewPending ? (
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary', flex: 'none' }}>Previewing…</Typography>
                ) : isNew || globalDraft ? null : (
                  <ButtonBase
                    onClick={() => showCardOnBoard(card.id)}
                    sx={{ fontSize: 12.5, fontWeight: 500, color: pane.accent, borderRadius: 0.5, flex: 'none' }}
                  >
                    Show saved card
                  </ButtonBase>
                )}
              </Stack>
            </Box>
          );
        })}
      </Stack>
      {draft && lensChangeSummary(lens, draft).length > 0 ? (
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
          Also: {lensChangeSummary(lens, draft).join(' · ')}
        </Typography>
      ) : null}
      {(candidate.isFetching || (previews.isFetching && shownCards.length === 0)) && (
        <Typography variant="body2" color="text.secondary">
          Preparing preview…
        </Typography>
      )}
      {candidate.error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void candidate.refetch()}>
              Retry
            </Button>
          }
        >
          Could not check this draft. Retry before saving.
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ '& .MuiButton-root': { whiteSpace: 'nowrap', flex: 'none' } }}
      >
        {canEdit && editGate && !editGate.isEditMode ? (
          // Editors review in edit mode; collaborators can submit their proposal directly.
          <Button variant="contained" disabled={busy} onClick={editGate.onEnterEdit}>
            Continue in edit
          </Button>
        ) : (
          <Button variant="contained" disabled={busy || !previewReady} onClick={() => void save()}>
            {canEdit ? 'Save changes' : 'Submit proposal'}
          </Button>
        )}
        <Button disabled={busy} onClick={() => void discard()}>
          {canEdit && editGate && !editGate.isEditMode ? 'Not now' : 'Discard'}
        </Button>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', textAlign: 'right', flex: 1, minWidth: 0 }}>
          {canEdit && editGate && !editGate.isEditMode
            ? 'Nothing is saved until you review it'
            : previews.error
              ? 'Save needs a preview'
              : previewReady && draftCardCount !== undefined
                ? `Draft, not saved · ${draftCardCount} card${draftCardCount === 1 ? '' : 's'}`
                : 'Draft, not saved · preview pending'}
        </Typography>
      </Stack>
    </Stack>
  );
}
